document.addEventListener("DOMContentLoaded", () => {
  const root = document.getElementById("clw-pcs-custom-fields");
  if (!root) return;

  const FABRIC_CDN =
    "https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js";
  const FREE_SHIPPING_THRESHOLD = 25;
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "svg"];
  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/svg+xml",
  ];

  // These are the exact business-provided quantity totals. They are base totals,
  // not per-item prices or incremental surcharges.
  const QUANTITY_TOTALS = {
    "2 Pack": 10.98,
    "4 Pack": 9.0,
    "6 Pack": 16.0,
    "10 Pack": 29.0,
    "20 Pack": 62.0,
    "50 Pack": 152.0,
  };
  const MATERIAL_SURCHARGES = {
    "Standard Vinyl": 0,
    Holographic: 4,
    Glitter: 7,
  };
  const CUTOUT_SURCHARGES = {
    "No Cutout": 0,
    "Lighter Logo Cutout": 3,
  };
  const ENHANCE_SURCHARGES = {
    "Keep Image as is": 0,
    "Enhance Quality": 4,
  };

  const state = {
    artworkFile: null,
    artworkSource: null,
    artworkTransform: null,
    selectedCutout: "No Cutout",
    lighterStickerMaterial: "Standard Vinyl",
    buyInBulk: "2 Pack",
    enhanceImage: "Keep Image as is",
    finalPreview: null,
    finalDesignFile: null,
    finalCutout: null,
    designDirty: false,
    currentTotal: 10.98,
    designerOpen: false,
    mockupReady: false,
  };

  let designer = null;
  let fabricLoadingPromise = null;
  let scrollLock = null;

  const byId = (id) => document.getElementById(id);
  const nextFrame = () =>
    new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  function getFileExtension(file) {
    const parts = String(file?.name || "").split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  }

  function isValidArtwork(file) {
    if (!file || file.size > MAX_FILE_SIZE) return false;
    const ext = getFileExtension(file);
    return ALLOWED_EXTENSIONS.includes(ext) || ALLOWED_MIME_TYPES.includes(file.type);
  }

  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("The artwork file could not be read."));
      reader.readAsDataURL(file);
    });
  }

  function dataURLToFile(dataURL, filename) {
    const parts = dataURL.split(",");
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (!mimeMatch || !parts[1]) throw new Error("The exported preview was invalid.");
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new File([bytes], filename, { type: mimeMatch[1] });
  }

  function loadImageElement(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      if (!String(src).startsWith("data:") && !String(src).startsWith("blob:")) {
        image.crossOrigin = "anonymous";
      }
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Could not load image: ${src}`));
      image.src = src;
    });
  }

  function isUnresolvedLiquid(value) {
    return !value || value.includes("{{") || value.includes("}}");
  }

  function resolveMockupURL(cutoutValue) {
    const card = root.querySelector(
      `.clw-pcs-cutout-card[data-cutout="${cutoutValue}"]`
    );
    if (!card) return "";
    const liquidURL = card.dataset.assetUrl || "";
    return isUnresolvedLiquid(liquidURL)
      ? card.dataset.fallbackUrl || ""
      : liquidURL;
  }

  function setupLocalAssetFallbacks() {
    root.querySelectorAll("img[data-fallback-src]").forEach((image) => {
      const fallback = image.dataset.fallbackSrc;
      if (isUnresolvedLiquid(image.getAttribute("src"))) image.src = fallback;
      image.addEventListener(
        "error",
        () => {
          if (image.getAttribute("src") !== fallback) image.src = fallback;
        },
        { once: true }
      );
    });
  }

  function ensureFabric() {
    if (window.fabric?.Canvas) return Promise.resolve(window.fabric);
    if (fabricLoadingPromise) return fabricLoadingPromise;

    fabricLoadingPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-clw-fabric="true"]');
      const script = existing || document.createElement("script");
      const timeout = window.setTimeout(() => {
        reject(new Error("The designer library took too long to load."));
      }, 15000);

      script.addEventListener(
        "load",
        () => {
          window.clearTimeout(timeout);
          if (window.fabric?.Canvas) resolve(window.fabric);
          else reject(new Error("The designer library did not initialize."));
        },
        { once: true }
      );
      script.addEventListener(
        "error",
        () => {
          window.clearTimeout(timeout);
          reject(new Error("The designer library could not be loaded."));
        },
        { once: true }
      );

      if (!existing) {
        script.src = FABRIC_CDN;
        script.async = true;
        script.dataset.clwFabric = "true";
        document.head.appendChild(script);
      }
    });
    return fabricLoadingPromise;
  }

  function lockPageScroll() {
    if (scrollLock) return;
    scrollLock = {
      scrollY: window.scrollY,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollLock.scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.documentElement.classList.add("clw-pcs-designer-open");
  }

  function unlockPageScroll() {
    if (!scrollLock) return;
    const saved = scrollLock;
    scrollLock = null;
    document.body.style.position = saved.position;
    document.body.style.top = saved.top;
    document.body.style.left = saved.left;
    document.body.style.right = saved.right;
    document.body.style.width = saved.width;
    document.documentElement.classList.remove("clw-pcs-designer-open");
    window.scrollTo(0, saved.scrollY);
  }

  function setMessage(element, message) {
    if (!element) return;
    element.textContent = message || "";
    element.classList.toggle("is-visible", Boolean(message));
  }

  function updatePropertyInputs() {
    const mappings = {
      "clw-pcs-property-cutout": state.selectedCutout,
      "clw-pcs-property-material": state.lighterStickerMaterial,
      "clw-pcs-property-bulk": state.buyInBulk,
      "clw-pcs-property-enhance":
        state.enhanceImage === "Enhance Quality"
          ? "(YES) Please Enhance Quality"
          : "Keep Image as is",
      "clw-pcs-property-design": state.finalDesignFile?.name || "",
    };
    Object.entries(mappings).forEach(([id, value]) => {
      const input = byId(id);
      if (input) input.value = value;
    });
  }

  function updateAddToCartAvailability() {
    const button = byId("clw-pcs-add-to-cart");
    if (!button) return;
    const ready = Boolean(
      state.artworkFile &&
        state.finalDesignFile &&
        state.finalPreview &&
        !state.designDirty
    );
    const loading = button.classList.contains("loading");
    button.disabled = !ready || loading;
    button.classList.toggle("clw-pcs-cart-btn--locked", !ready);
    button.setAttribute("aria-disabled", String(!ready || loading));
    button.title = ready
      ? ""
      : "Finish your lighter wrap design before adding to cart.";
  }

  function recalculatePrice() {
    const total =
      (QUANTITY_TOTALS[state.buyInBulk] ?? QUANTITY_TOTALS["2 Pack"]) +
      (MATERIAL_SURCHARGES[state.lighterStickerMaterial] || 0) +
      (CUTOUT_SURCHARGES[state.selectedCutout] || 0) +
      (ENHANCE_SURCHARGES[state.enhanceImage] || 0);
    state.currentTotal = Math.round(total * 100) / 100;

    const price = byId("clw-pcs-final-price");
    if (price) price.textContent = state.currentTotal.toFixed(2);

    const activeQuantity = root.querySelector(
      "#bulk-variant .sticker-variant.active"
    );
    const wraps = activeQuantity?.dataset.wraps || "2";
    const label = byId("clw-pcs-total-label");
    if (label) label.textContent = `TOTAL · ${wraps} WRAPS`;

    const shipping = byId("clw-pcs-shipping");
    const shippingText = byId("clw-pcs-shipping-text");
    const shippingFill = byId("clw-pcs-shipping-fill");
    const remaining = FREE_SHIPPING_THRESHOLD - state.currentTotal;
    if (shipping && shippingText && shippingFill) {
      const free = remaining <= 0;
      shipping.classList.toggle("is-free", free);
      shippingText.innerHTML = free
        ? "You unlocked <strong>free shipping</strong>"
        : `Add <strong>$${remaining.toFixed(2)}</strong> more for <strong>free shipping</strong>`;
      shippingFill.style.width = free
        ? "100%"
        : `${Math.max(0, Math.min(100, (state.currentTotal / FREE_SHIPPING_THRESHOLD) * 100))}%`;
    }
    updatePropertyInputs();
  }

  function markDesignDirty() {
    if (state.finalPreview) state.designDirty = true;
    updateAddToCartAvailability();
  }

  function updateUploadUI() {
    const empty = byId("clw-pcs-designer-upload-empty");
    const ready = byId("clw-pcs-designer-upload-ready");
    const thumb = byId("clw-pcs-artwork-thumb");
    const name = byId("clw-pcs-artwork-name");
    const hasArtwork = Boolean(state.artworkFile && state.artworkSource);
    if (empty) empty.hidden = hasArtwork;
    if (ready) ready.hidden = !hasArtwork;
    if (thumb) thumb.src = hasArtwork ? state.artworkSource : "";
    if (name) name.textContent = hasArtwork ? state.artworkFile.name : "Artwork ready";
  }

  function updateFinishedPreview() {
    const empty = byId("clw-pcs-design-empty");
    const complete = byId("clw-pcs-design-complete");
    const image = byId("clw-pcs-final-preview");
    const hasPreview = Boolean(state.finalPreview);
    if (empty) empty.hidden = hasPreview;
    if (complete) complete.hidden = !hasPreview;
    if (image) image.src = hasPreview ? state.finalPreview : "";
    updatePropertyInputs();
    updateAddToCartAvailability();
  }

  class LighterWrapDesigner {
    constructor() {
      this.canvas = null;
      this.artwork = null;
      this.mockup = null;
      this.surface = null;
      this.guides = [];
      this.frameBounds = null;
      this.printBounds = null;
      this.mockupToken = 0;
      this.resizeObserver = null;
      this.snappingEnabled = true;
    }

    init() {
      const container = byId("clw-pcs-canvas-container");
      if (!container || this.canvas) return;
      container.innerHTML = '<canvas id="clw-pcs-design-canvas"></canvas>';
      this.canvas = new fabric.Canvas("clw-pcs-design-canvas", {
        width: Math.max(1, container.clientWidth),
        height: Math.max(1, container.clientHeight),
        backgroundColor: "#eef0f3",
        preserveObjectStacking: true,
        controlsAboveOverlay: true,
        selection: false,
      });
      this.bindCanvasEvents();
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(container);
      } else {
        window.addEventListener("resize", () => this.resize(), { passive: true });
      }
      this.resize();
    }

    bindCanvasEvents() {
      this.canvas.on("selection:created", () => this.updateAlignmentState());
      this.canvas.on("selection:updated", () => this.updateAlignmentState());
      this.canvas.on("selection:cleared", () => this.updateAlignmentState());
      this.canvas.on("object:moving", (event) => this.snapArtwork(event.target));
      this.canvas.on("object:modified", (event) => {
        if (event.target !== this.artwork) return;
        state.artworkTransform = this.captureTransform();
        markDesignDirty();
      });
    }

    buildBodyPath() {
      const frame = this.frameBounds;
      if (!frame) return "";
      const point = (x, y) => `${frame.left + x * frame.size} ${frame.top + y * frame.size}`;
      return `M ${point(0.252, 0.278)} L ${point(0.767, 0.278)} L ${point(
        0.758,
        0.854
      )} L ${point(0.233, 0.854)} Z`;
    }

    makeClipPath() {
      return new fabric.Path(this.buildBodyPath(), {
        absolutePositioned: true,
        fill: "#000",
        strokeWidth: 0,
        selectable: false,
        evented: false,
      });
    }

    captureTransform() {
      if (!this.artwork || !this.printBounds) return null;
      const center = this.artwork.getCenterPoint();
      const bounds = this.printBounds;
      return {
        centerX: (center.x - bounds.left) / bounds.width,
        centerY: (center.y - bounds.top) / bounds.height,
        widthRatio: this.artwork.getScaledWidth() / bounds.width,
        heightRatio: this.artwork.getScaledHeight() / bounds.height,
        angle: this.artwork.angle || 0,
        flipX: Boolean(this.artwork.flipX),
        flipY: Boolean(this.artwork.flipY),
      };
    }

    applyTransform(snapshot) {
      if (!this.artwork || !this.printBounds || !snapshot) return;
      const bounds = this.printBounds;
      this.artwork.set({
        originX: "center",
        originY: "center",
        left: bounds.left + snapshot.centerX * bounds.width,
        top: bounds.top + snapshot.centerY * bounds.height,
        scaleX: (snapshot.widthRatio * bounds.width) / this.artwork.width,
        scaleY: (snapshot.heightRatio * bounds.height) / this.artwork.height,
        angle: snapshot.angle || 0,
        flipX: Boolean(snapshot.flipX),
        flipY: Boolean(snapshot.flipY),
      });
      this.artwork.setCoords();
    }

    resize() {
      if (!this.canvas) return;
      const container = byId("clw-pcs-canvas-container");
      if (!container || container.clientWidth < 2 || container.clientHeight < 2) return;

      const snapshot = this.captureTransform() || state.artworkTransform;
      const width = Math.max(1, container.clientWidth);
      const height = Math.max(1, container.clientHeight);
      this.canvas.setDimensions({ width, height });

      const size = Math.max(220, Math.min(width * 0.92, height * 0.92, 820));
      this.frameBounds = {
        left: (width - size) / 2,
        top: (height - size) / 2,
        size,
      };
      this.printBounds = {
        left: this.frameBounds.left + this.frameBounds.size * 0.233,
        top: this.frameBounds.top + this.frameBounds.size * 0.278,
        width: this.frameBounds.size * (0.767 - 0.233),
        height: this.frameBounds.size * (0.854 - 0.278),
      };

      if (this.surface) this.canvas.remove(this.surface);
      this.surface = new fabric.Path(this.buildBodyPath(), {
        fill: "#fff",
        strokeWidth: 0,
        selectable: false,
        evented: false,
      });
      this.canvas.add(this.surface);

      if (this.artwork) {
        this.artwork.clipPath = this.makeClipPath();
        if (snapshot) this.applyTransform(snapshot);
      }
      if (this.mockup) this.positionMockup();
      this.drawGuides();
      this.enforceLayerOrder();
      this.canvas.calcOffset();
      this.canvas.requestRenderAll();
      state.artworkTransform = this.captureTransform() || state.artworkTransform;
    }

    positionMockup() {
      if (!this.mockup || !this.frameBounds) return;
      const scale = this.frameBounds.size / this.mockup.width;
      this.mockup.set({
        left: this.frameBounds.left,
        top: this.frameBounds.top,
        originX: "left",
        originY: "top",
        scaleX: scale,
        scaleY: scale,
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
      });
      this.mockup.setCoords();
    }

    enforceLayerOrder() {
      if (!this.canvas) return;
      let index = 0;
      if (this.surface && this.canvas.getObjects().includes(this.surface)) {
        this.canvas.moveTo(this.surface, index);
        index += 1;
      }
      if (this.artwork && this.canvas.getObjects().includes(this.artwork)) {
        this.canvas.moveTo(this.artwork, index);
        index += 1;
      }
      if (this.mockup && this.canvas.getObjects().includes(this.mockup)) {
        this.canvas.moveTo(this.mockup, index);
        index += 1;
      }
      this.guides.forEach((guide) => {
        if (this.canvas.getObjects().includes(guide)) {
          this.canvas.moveTo(guide, index);
          index += 1;
        }
      });
    }

    drawGuides() {
      if (!this.canvas || !this.printBounds) return;
      this.guides.forEach((guide) => this.canvas.remove(guide));
      this.guides = [];
      const visible = Boolean(byId("clw-pcs-toggle-guides")?.checked);
      if (!visible) return;

      const outline = new fabric.Path(this.buildBodyPath(), {
        fill: "transparent",
        stroke: "#f0a028",
        strokeWidth: 2,
        strokeDashArray: [8, 6],
        strokeUniform: true,
        selectable: false,
        evented: false,
      });
      const centerX = this.printBounds.left + this.printBounds.width / 2;
      const centerY = this.printBounds.top + this.printBounds.height / 2;
      const vertical = new fabric.Line(
        [centerX, this.printBounds.top, centerX, this.printBounds.top + this.printBounds.height],
        {
          stroke: "rgba(240,160,40,.85)",
          strokeWidth: 1,
          strokeDashArray: [6, 5],
          selectable: false,
          evented: false,
        }
      );
      const horizontal = new fabric.Line(
        [this.printBounds.left, centerY, this.printBounds.left + this.printBounds.width, centerY],
        {
          stroke: "rgba(240,160,40,.85)",
          strokeWidth: 1,
          strokeDashArray: [6, 5],
          selectable: false,
          evented: false,
        }
      );
      this.guides = [outline, vertical, horizontal];
      this.guides.forEach((guide) => this.canvas.add(guide));
      this.enforceLayerOrder();
      this.canvas.requestRenderAll();
    }

    toggleGuides(visible) {
      if (visible) this.drawGuides();
      else {
        this.guides.forEach((guide) => this.canvas.remove(guide));
        this.guides = [];
        this.canvas.requestRenderAll();
      }
    }

    async loadMockup(cutoutValue) {
      if (!this.canvas) return false;
      const token = ++this.mockupToken;
      state.mockupReady = false;
      this.updateFinishButtonState();
      const url = resolveMockupURL(cutoutValue);
      if (!url) {
        this.showError("The selected lighter mockup asset is missing.");
        return false;
      }

      try {
        const imageElement = await loadImageElement(url);
        if (token !== this.mockupToken) return false;
        const nextMockup = new fabric.Image(imageElement, {
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          objectCaching: true,
        });
        if (this.mockup) this.canvas.remove(this.mockup);
        this.mockup = nextMockup;
        this.positionMockup();
        this.canvas.add(this.mockup);
        this.enforceLayerOrder();
        state.mockupReady = true;
        this.updateFinishButtonState();
        this.canvas.requestRenderAll();
        setMessage(byId("clw-pcs-canvas-message"), "");
        return true;
      } catch (error) {
        if (token !== this.mockupToken) return false;
        console.error("Lighter mockup load failed:", error);
        if (this.mockup) this.canvas.remove(this.mockup);
        this.mockup = null;
        state.mockupReady = false;
        this.updateFinishButtonState();
        this.showError("The lighter mockup could not be loaded. Please try again.");
        return false;
      }
    }

    async setArtwork(source, snapshot = null) {
      if (!this.canvas || !this.printBounds) {
        throw new Error("The design canvas is not ready yet.");
      }
      const imageElement = await loadImageElement(source);
      const image = new fabric.Image(imageElement, {
        originX: "center",
        originY: "center",
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        transparentCorners: false,
        cornerColor: "#f0a028",
        cornerStrokeColor: "#111114",
        cornerStyle: "circle",
        borderColor: "#f0a028",
        borderScaleFactor: 1.5,
        cornerSize: window.matchMedia("(max-width: 768px)").matches ? 22 : 18,
        padding: 2,
        lockUniScaling: true,
      });
      image.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
      image.clipPath = this.makeClipPath();

      if (this.artwork) this.canvas.remove(this.artwork);
      this.artwork = image;
      this.canvas.add(this.artwork);

      if (snapshot) {
        this.applyTransform(snapshot);
      } else {
        const scale = Math.max(
          this.printBounds.width / image.width,
          this.printBounds.height / image.height
        );
        image.set({
          left: this.printBounds.left + this.printBounds.width / 2,
          top: this.printBounds.top + this.printBounds.height / 2,
          scaleX: scale,
          scaleY: scale,
        });
        image.setCoords();
      }

      this.canvas.setActiveObject(this.artwork);
      this.enforceLayerOrder();
      state.artworkTransform = this.captureTransform();
      this.updateAlignmentState();
      this.updateFinishButtonState();
      this.canvas.requestRenderAll();
    }

    removeArtwork() {
      if (!this.canvas) return;
      if (this.artwork) this.canvas.remove(this.artwork);
      this.artwork = null;
      state.artworkTransform = null;
      this.canvas.discardActiveObject();
      this.updateAlignmentState();
      this.updateFinishButtonState();
      this.canvas.requestRenderAll();
    }

    selectArtwork() {
      if (!this.canvas || !this.artwork) return;
      this.canvas.setActiveObject(this.artwork);
      this.updateAlignmentState();
      this.canvas.requestRenderAll();
    }

    snapArtwork(target) {
      if (
        !this.snappingEnabled ||
        target !== this.artwork ||
        !this.printBounds
      ) {
        return;
      }
      target.setCoords();
      const rect = target.getBoundingRect(true);
      const bounds = this.printBounds;
      const threshold = Math.max(7, this.frameBounds.size * 0.012);
      const xAdjustments = [
        bounds.left - rect.left,
        bounds.left + bounds.width / 2 - (rect.left + rect.width / 2),
        bounds.left + bounds.width - (rect.left + rect.width),
      ];
      const yAdjustments = [
        bounds.top - rect.top,
        bounds.top + bounds.height / 2 - (rect.top + rect.height / 2),
        bounds.top + bounds.height - (rect.top + rect.height),
      ];
      const nearest = (values) =>
        values.reduce(
          (best, value) =>
            Math.abs(value) < Math.abs(best) ? value : best,
          Number.POSITIVE_INFINITY
        );
      const dx = nearest(xAdjustments);
      const dy = nearest(yAdjustments);
      if (Math.abs(dx) <= threshold) target.left += dx;
      if (Math.abs(dy) <= threshold) target.top += dy;
      target.setCoords();
      this.canvas.requestRenderAll();
    }

    alignArtwork(direction) {
      if (!this.canvas || !this.artwork || !this.printBounds) return;
      if (this.canvas.getActiveObject() !== this.artwork) return;
      this.artwork.setCoords();
      const rect = this.artwork.getBoundingRect(true);
      const bounds = this.printBounds;
      let dx = 0;
      let dy = 0;
      if (direction === "left") dx = bounds.left - rect.left;
      if (direction === "centerH") {
        dx = bounds.left + bounds.width / 2 - (rect.left + rect.width / 2);
      }
      if (direction === "right") {
        dx = bounds.left + bounds.width - (rect.left + rect.width);
      }
      if (direction === "top") dy = bounds.top - rect.top;
      if (direction === "centerV") {
        dy = bounds.top + bounds.height / 2 - (rect.top + rect.height / 2);
      }
      if (direction === "bottom") {
        dy = bounds.top + bounds.height - (rect.top + rect.height);
      }
      this.artwork.set({ left: this.artwork.left + dx, top: this.artwork.top + dy });
      this.artwork.setCoords();
      state.artworkTransform = this.captureTransform();
      markDesignDirty();
      this.canvas.requestRenderAll();
    }

    updateAlignmentState() {
      const enabled = Boolean(
        this.canvas && this.artwork && this.canvas.getActiveObject() === this.artwork
      );
      root.querySelectorAll(".clw-pcs-align-btn").forEach((button) => {
        button.disabled = !enabled;
      });
    }

    updateFinishButtonState() {
      const button = byId("clw-pcs-finish-editing");
      if (button) button.disabled = !(this.artwork && state.artworkFile && state.mockupReady);
    }

    showError(message) {
      setMessage(byId("clw-pcs-canvas-message"), message);
    }

    async finishEditing() {
      if (!this.canvas || !this.artwork || !state.artworkFile) {
        this.showError("Upload a valid artwork image before finishing.");
        return;
      }
      if (!this.mockup || !state.mockupReady) {
        this.showError("The lighter mockup is not ready yet. Please try again.");
        return;
      }

      const button = byId("clw-pcs-finish-editing");
      const guideSetting = Boolean(byId("clw-pcs-toggle-guides")?.checked);
      state.artworkTransform = this.captureTransform();
      this.canvas.discardActiveObject();
      this.guides.forEach((guide) => {
        guide.visible = false;
      });
      this.enforceLayerOrder();
      this.canvas.requestRenderAll();

      if (button) {
        button.disabled = true;
        button.classList.add("is-saving");
        button.textContent = "Saving…";
      }

      try {
        const frame = this.frameBounds;
        const multiplier = Math.max(2, Math.min(3, 1500 / frame.size));
        const dataURL = this.canvas.toDataURL({
          left: frame.left,
          top: frame.top,
          width: frame.size,
          height: frame.size,
          format: "png",
          multiplier,
          enableRetinaScaling: true,
        });
        const finalFile = dataURLToFile(dataURL, "custom-lighter-wrap-design.png");
        state.finalPreview = dataURL;
        state.finalDesignFile = finalFile;
        state.finalCutout = state.selectedCutout;
        state.designDirty = false;
        updateFinishedPreview();
        closeDesigner();
      } catch (error) {
        console.error("Lighter wrap export failed:", error);
        this.showError("Your preview could not be saved. Please try again.");
      } finally {
        this.guides.forEach((guide) => {
          guide.visible = guideSetting;
        });
        this.canvas.requestRenderAll();
        if (button) {
          button.classList.remove("is-saving");
          button.textContent = "Finish Editing";
        }
        this.updateFinishButtonState();
      }
    }
  }

  async function openDesigner() {
    if (state.designerOpen) return;
    const overlay = byId("clw-pcs-designer");
    const loading = byId("clw-pcs-canvas-loading");
    if (!overlay) return;

    state.designerOpen = true;
    overlay.hidden = false;
    overlay.classList.add("is-open");
    if (loading) loading.hidden = false;
    lockPageScroll();
    setMessage(byId("clw-pcs-design-error"), "");

    try {
      await ensureFabric();
      await nextFrame();
      if (!designer) {
        designer = new LighterWrapDesigner();
        designer.init();
      } else {
        designer.resize();
      }

      await designer.loadMockup(state.selectedCutout);
      if (state.artworkSource && !designer.artwork) {
        await designer.setArtwork(state.artworkSource, state.artworkTransform);
      }
      updateUploadUI();
      if (designer.artwork) designer.selectArtwork();
    } catch (error) {
      console.error("Designer initialization failed:", error);
      setMessage(
        byId("clw-pcs-design-error"),
        "The designer could not start. Check your connection and try again."
      );
      closeDesigner();
    } finally {
      if (loading) loading.hidden = true;
    }
  }

  function closeDesigner() {
    const overlay = byId("clw-pcs-designer");
    if (!overlay) return;
    if (designer?.artwork) state.artworkTransform = designer.captureTransform();
    overlay.classList.remove("is-open");
    overlay.hidden = true;
    state.designerOpen = false;
    unlockPageScroll();
  }

  async function handleArtworkFile(file) {
    const error = byId("clw-pcs-artwork-error");
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setMessage(error, "Please choose an artwork file smaller than 50 MB.");
      return;
    }
    if (!isValidArtwork(file)) {
      setMessage(error, "Use a JPG, JPEG, PNG, WEBP, or SVG artwork file.");
      return;
    }
    if (!designer?.canvas) {
      setMessage(error, "The design canvas is still loading. Please try again.");
      return;
    }

    const previousTransform = designer.artwork
      ? designer.captureTransform()
      : state.artworkTransform;
    try {
      const source = await readFileAsDataURL(file);
      await designer.setArtwork(source, designer.artwork ? previousTransform : null);
      state.artworkFile = file;
      state.artworkSource = source;
      state.artworkTransform = designer.captureTransform();
      state.designDirty = Boolean(state.finalPreview);
      setMessage(error, "");
      setMessage(byId("clw-pcs-canvas-message"), "");
      updateUploadUI();
      designer.updateFinishButtonState();
      updateAddToCartAvailability();
    } catch (loadError) {
      console.error("Artwork load failed:", loadError);
      setMessage(error, "That image could not be loaded. Try exporting it as PNG or JPG.");
    }
  }

  function clearFinishedDesign() {
    state.finalPreview = null;
    state.finalDesignFile = null;
    state.finalCutout = null;
    state.designDirty = false;
    updateFinishedPreview();
  }

  function deleteArtwork() {
    const input = byId("clw-pcs-artwork-file");
    if (input) input.value = "";
    if (designer) designer.removeArtwork();
    state.artworkFile = null;
    state.artworkSource = null;
    state.artworkTransform = null;
    clearFinishedDesign();
    updateUploadUI();
    setMessage(byId("clw-pcs-artwork-error"), "");
  }

  async function selectCutout(value) {
    if (!(value in CUTOUT_SURCHARGES)) return;
    const changed = state.selectedCutout !== value;
    state.selectedCutout = value;
    root.querySelectorAll(".clw-pcs-cutout-card").forEach((card) => {
      const active = card.dataset.cutout === value;
      card.classList.toggle("active", active);
      card.setAttribute("aria-checked", String(active));
    });
    if (changed) markDesignDirty();
    recalculatePrice();
    if (designer?.canvas) await designer.loadMockup(value);
  }

  function selectSingleOption(wrapper, selected) {
    wrapper.querySelectorAll(".sticker-variant").forEach((option) => {
      option.classList.toggle("active", option === selected);
    });
  }

  function setupProductOptions() {
    const quantity = byId("bulk-variant");
    const material = byId("material-variant");
    const enhance = byId("enhance-variant");

    quantity?.addEventListener("click", (event) => {
      const selected = event.target.closest(".sticker-variant[data-bulk]");
      if (!selected || !quantity.contains(selected)) return;
      selectSingleOption(quantity, selected);
      state.buyInBulk = selected.dataset.bulk;
      recalculatePrice();
    });

    material?.addEventListener("click", (event) => {
      const selected = event.target.closest(".sticker-variant[data-material]");
      if (!selected || !material.contains(selected)) return;
      selectSingleOption(material, selected);
      state.lighterStickerMaterial = selected.dataset.material;
      recalculatePrice();
    });

    enhance?.addEventListener("click", (event) => {
      const selected = event.target.closest(".sticker-variant[data-enhance]");
      if (!selected || !enhance.contains(selected)) return;
      selectSingleOption(enhance, selected);
      state.enhanceImage = selected.dataset.enhance;
      recalculatePrice();
    });

    root.querySelector(".clw-pcs-cutout-grid")?.addEventListener("click", (event) => {
      const selected = event.target.closest(".clw-pcs-cutout-card[data-cutout]");
      if (!selected) return;
      selectCutout(selected.dataset.cutout);
    });
  }

  function setupDesignerControls() {
    const fileInput = byId("clw-pcs-artwork-file");
    const uploadDrop = byId("clw-pcs-designer-upload-empty");
    const choose = byId("clw-pcs-choose-artwork");
    const replace = byId("clw-pcs-replace-artwork");

    byId("clw-pcs-open-designer")?.addEventListener("click", openDesigner);
    byId("clw-pcs-edit-design")?.addEventListener("click", openDesigner);
    byId("clw-pcs-close-designer")?.addEventListener("click", closeDesigner);
    byId("clw-pcs-finish-editing")?.addEventListener("click", () =>
      designer?.finishEditing()
    );
    const openFilePicker = () => {
      if (!fileInput) return;
      // Clearing first lets a customer deliberately choose the same file again.
      fileInput.value = "";
      fileInput.click();
    };
    choose?.addEventListener("click", openFilePicker);
    replace?.addEventListener("click", openFilePicker);
    byId("clw-pcs-select-artwork")?.addEventListener("click", () =>
      designer?.selectArtwork()
    );
    byId("clw-pcs-delete-artwork")?.addEventListener("click", deleteArtwork);

    fileInput?.addEventListener("change", (event) => {
      const file = event.target.files?.[0];
      handleArtworkFile(file);
    });

    ["dragenter", "dragover"].forEach((type) => {
      uploadDrop?.addEventListener(type, (event) => {
        event.preventDefault();
        uploadDrop.classList.add("is-dragover");
      });
    });
    ["dragleave", "drop"].forEach((type) => {
      uploadDrop?.addEventListener(type, (event) => {
        event.preventDefault();
        uploadDrop.classList.remove("is-dragover");
      });
    });
    uploadDrop?.addEventListener("drop", (event) => {
      const file = event.dataTransfer?.files?.[0];
      if (file) handleArtworkFile(file);
    });

    byId("clw-pcs-toggle-guides")?.addEventListener("change", (event) => {
      designer?.toggleGuides(event.target.checked);
    });
    byId("clw-pcs-toggle-snapping")?.addEventListener("change", (event) => {
      if (designer) designer.snappingEnabled = event.target.checked;
    });
    root.querySelectorAll(".clw-pcs-align-btn").forEach((button) => {
      button.addEventListener("click", () => designer?.alignArtwork(button.dataset.align));
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && state.designerOpen) closeDesigner();
    });
  }

  const CART_BUTTON_HTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.5"></circle><circle cx="18" cy="20" r="1.5"></circle><path d="M2 3h3l2.4 12.4a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L23 6H6"></path></svg> Add to cart`;
  const CART_LOADING_HTML = `<span class="clw-pcs-cart-loading"><svg class="clw-pcs-cart-loading__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.22-8.56"></path></svg>Adding to cart</span>`;

  async function refreshMiniCart(cartAddJson, shopifyVariantId) {
    const cartJson = {
      items: [cartAddJson],
      item_count: 1,
      total_price: cartAddJson.final_price,
      original_total_price: cartAddJson.original_price,
      total_discount: 0,
    };
    const miniCartHTML = await fetch(`/cart?section_id=mini-cart&t=${Date.now()}`)
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        return wrapper.querySelector("cart-drawer")?.outerHTML;
      });
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && miniCartHTML) {
      cartDrawer.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: { ...cartJson, sections: { "mini-cart": miniCartHTML } },
            openMiniCart: true,
          },
        })
      );
    }
    window.setTimeout(() => {
      cartDrawer?.querySelectorAll("line-item").forEach((lineItem) => {
        const priceNode =
          lineItem.querySelector(".line-item__price-list-container .price") ||
          lineItem.querySelector(".product-item-meta__price-list-container .price");
        if (!priceNode || !/Free|\$0\.00/.test(priceNode.textContent.trim())) return;
        const variantId =
          lineItem.querySelector("a[href*='?variant=']")?.href.split("variant=")[1]?.split("&")[0] ||
          lineItem.querySelector("a[href*='/cart/change?id=']")?.href.match(/id=(\d+):/)?.[1];
        if (variantId && String(variantId) === String(shopifyVariantId)) {
          priceNode.innerHTML = `<span class="visually-hidden">Sale price</span>$${(
            cartAddJson.price / 100
          ).toFixed(2)}`;
        }
      });
    }, 300);
    window.setTimeout(async () => {
      try {
        const cart = await fetch("/cart.js").then((response) => response.json());
        const count = document.querySelector("cart-count");
        if (count) count.textContent = cart.item_count;
      } catch (error) {
        console.warn("Cart count refresh failed:", error);
      }
    }, 300);
  }

  function resetAfterSuccessfulCart() {
    closeDesigner();
    if (designer) designer.removeArtwork();
    Object.assign(state, {
      artworkFile: null,
      artworkSource: null,
      artworkTransform: null,
      finalPreview: null,
      finalDesignFile: null,
      finalCutout: null,
      designDirty: false,
      selectedCutout: "No Cutout",
      lighterStickerMaterial: "Standard Vinyl",
      buyInBulk: "2 Pack",
      enhanceImage: "Keep Image as is",
    });
    const fileInput = byId("clw-pcs-artwork-file");
    if (fileInput) fileInput.value = "";
    root.querySelectorAll(".clw-pcs-cutout-card").forEach((card) => {
      const active = card.dataset.cutout === "No Cutout";
      card.classList.toggle("active", active);
      card.setAttribute("aria-checked", String(active));
    });
    [
      ["#bulk-variant .sticker-variant", "bulk", "2 Pack"],
      ["#material-variant .sticker-variant", "material", "Standard Vinyl"],
      ["#enhance-variant .sticker-variant", "enhance", "Keep Image as is"],
    ].forEach(([selector, key, value]) => {
      root.querySelectorAll(selector).forEach((option) => {
        option.classList.toggle("active", option.dataset[key] === value);
      });
    });
    if (designer?.canvas) designer.loadMockup("No Cutout");
    updateUploadUI();
    updateFinishedPreview();
    recalculatePrice();
  }

  function setupApippaAddToCart() {
    const button = byId("clw-pcs-add-to-cart");
    if (!button) return;
    button.addEventListener("click", async (event) => {
      event.preventDefault();
      if (!state.artworkFile || !state.finalDesignFile || !state.finalPreview || state.designDirty) {
        setMessage(
          byId("clw-pcs-design-error"),
          "Finish and save your lighter wrap design before adding it to cart."
        );
        byId("artwork-variant")?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      button.disabled = true;
      button.classList.add("loading");
      button.innerHTML = CART_LOADING_HTML;
      let addedSuccessfully = false;
      try {
        // Keep these Apipa identifiers, property names, and outgoing values stable.
        const requestData = {
          calculatorId: "ee7575a05e484",
          productId: 7693581877296,
          properties: {
            "Upload Image": state.artworkFile?.name,
            "Lighter Logo Cutout": state.selectedCutout,
            "Lighter Sticker Material": state.lighterStickerMaterial,
            "Buy In Bulk": state.buyInBulk,
            "Enhance Image":
              state.enhanceImage === "Enhance Quality"
                ? "(YES) Please Enhance Quality"
                : "Keep Image as is",
          },
        };
        const variantResponse = await fetch(
          "https://api.custompricecalculator.com/api/v1/variant",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData),
          }
        );
        if (!variantResponse.ok) throw new Error("The calculator request failed.");
        const variantJSON = await variantResponse.json();
        const shopifyVariantId =
          typeof variantJSON === "string"
            ? variantJSON
            : variantJSON?.variantId || variantJSON?.id;
        if (!shopifyVariantId) throw new Error("The calculator returned an invalid variant.");
        const formData = new FormData();
        formData.append("id", shopifyVariantId);
        formData.append("quantity", 1);
        formData.append("properties[Upload Image]", state.artworkFile);
        Object.entries(requestData.properties).forEach(([key, value]) => {
          if (key !== "Upload Image") formData.append(`properties[${key}]`, value);
        });
        // This Shopify line-item file supplements the unchanged Apipa payload.
        formData.append("properties[Final Design]", state.finalDesignFile);
        const cartResponse = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });
        const cartItem = await cartResponse.json();
        if (!cartResponse.ok) throw new Error("Shopify could not add this design to cart.");
        addedSuccessfully = true;
        await refreshMiniCart(cartItem, shopifyVariantId);
      } catch (error) {
        console.error("Apippa Add-to-Cart Error:", error);
        window.alert("There was a problem adding your item to the cart. Please try again.");
      } finally {
        button.classList.remove("loading");
        button.innerHTML = CART_BUTTON_HTML;
        if (addedSuccessfully) resetAfterSuccessfulCart();
        updateAddToCartAvailability();
      }
    });
  }

  function initialize() {
    setupLocalAssetFallbacks();
    setupProductOptions();
    setupDesignerControls();
    setupApippaAddToCart();
    byId("clw-pcs-remove-design")?.addEventListener("click", () => {
      deleteArtwork();
      selectCutout("No Cutout");
    });
    updateUploadUI();
    updateFinishedPreview();
    recalculatePrice();
  }

  initialize();
});
